import { Typography } from "@mui/material";
import * as React from "react";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useApi } from "../../containers/ApiContainer";
import { useLocationsState } from "../../containers/LocationsStateContainer";
import { TenantService } from "../../services/TenantService";
import { Hangar, Location, Ramp, Stack, Tenant } from "../../types";
import { usePermissions } from "../../hooks/usePermissions";
import { formatRoleName, Permission } from "../../utils/permissions";
import { ConfirmationDialog } from "../../widgets/ConfirmationDialog";
import { EditTenantPresenter } from "./EditTenantPresenter";

type Props = {
  displayTypeAndLocation?: boolean;
  stack?: Stack;
  setStack?: (h: Stack) => void;
  tenant: Tenant;
  setTenant: (tenant: Tenant) => void;
  onClose: () => void;
};

export const EditTenant: React.FC<Props> = ({
  displayTypeAndLocation = false,
  stack,
  setStack,
  tenant,
  setTenant,
  onClose,
}) => {
  const { activeFBO } = useActiveFBOs();
  const { hasPermission } = usePermissions();
  const { postgrest } = useApi();
  const { locations, findTenantLocations, sendToLocation } =
    useLocationsState();
  const [localTenant, setLocalTenant] = React.useState<Tenant>(tenant);
  const [localCurrentLocation, setLocalCurrentLocation] =
    React.useState<Location<Ramp | Hangar> | null>();
  const [localAssignedLocation, setLocalAssignedLocation] =
    React.useState<Location<Ramp | Hangar> | null>();

  const tenantService = React.useMemo(
    () => new TenantService(postgrest),
    [postgrest]
  );

  React.useEffect(() => {
    const { current, assigned } = findTenantLocations(tenant);
    setLocalCurrentLocation(current);
    setLocalAssignedLocation(assigned);
  }, [tenant, locations]);

  const onSave = async () => {
    const { current, assigned } = findTenantLocations(tenant);
    if (Boolean(stack) && current?.id !== localCurrentLocation?.id) {
      setStack({
        ...stack,
        tenants: stack.tenants.filter((t) => t.id !== localTenant.id),
      });
    }
    if (Boolean(stack) && assigned?.id !== localAssignedLocation?.id) {
      setStack({
        ...stack,
        tenants: stack.tenants.filter((t) => t.id !== localTenant.id),
      });
    }

    // Persist tenant field changes to database
    const tenantUpdates: {
      tail_number?: string;
      owner_manager?: string;
      aircraft_id?: string;
      note?: string;
      note_last_updated?: string;
      note_last_updated_by?: string;
    } = {};
    let needTenantUpdate = false;

    if (localTenant.tail_number !== tenant.tail_number) {
      tenantUpdates.tail_number = localTenant.tail_number;
      needTenantUpdate = true;
    }
    if (localTenant.owner_manager !== tenant.owner_manager) {
      tenantUpdates.owner_manager = localTenant.owner_manager;
      needTenantUpdate = true;
    }
    if (localTenant.aircraft_id !== tenant.aircraft_id) {
      tenantUpdates.aircraft_id = localTenant.aircraft_id;
      needTenantUpdate = true;
    }
    if (localTenant.note !== tenant.note) {
      tenantUpdates.note = localTenant.note || null;
      tenantUpdates.note_last_updated = localTenant.note_last_updated;
      tenantUpdates.note_last_updated_by = localTenant.note_last_updated_by;
      needTenantUpdate = true;
    }

    if (needTenantUpdate) {
      await tenantService.updateTenant(localTenant.id, tenantUpdates);
    }

    setTenant(localTenant);
    // TODO: could this be simplified / get rid of sendToLocation?
    if (current?.id !== localCurrentLocation?.id) {
      await sendToLocation(localCurrentLocation, "current", localTenant);
    }
    if (assigned?.id !== localAssignedLocation?.id) {
      await sendToLocation(localAssignedLocation, "reference", localTenant);
    }
  };

  if (!hasPermission(Permission.MODIFY_TENANTS) && tenant.type === "base") {
    return (
      <ConfirmationDialog
        title={"Insufficient Permission"}
        text={
          <Typography variant="body1">
            Insufficient permission to modify a base tenant. Currently you have
            the role of <u>{formatRoleName(activeFBO.user_role)}</u>. You need
            to be an <u>FBO Admin</u> or <u>Manager</u> to modify a base tenant.
            Please contact your AirPlx admin if you think this is a mistake.
          </Typography>
        }
        onOk={function (): void {
          // nothing to do
        }}
        onClose={function (): void {
          onClose();
        }}
      />
    );
  }

  return (
    <EditTenantPresenter
      displayTypeAndLocation={displayTypeAndLocation}
      isReference={stack?.isReference ?? false}
      tenant={localTenant}
      setTenant={setLocalTenant}
      currentLocation={localCurrentLocation}
      setCurrentLocation={setLocalCurrentLocation}
      assignedLocation={localAssignedLocation}
      setAssignedLocation={setLocalAssignedLocation}
      onSave={onSave}
      onClose={onClose}
    />
  );
};
